import React from 'react';
import { useForm } from 'react-hook-form';
import { signInWithGoogle, signIn, getToken } from '@app/auth/firebase';
import { useCreateUserMutation, useAddOrRemoveToken } from '@app/graphql';
import { useLocation } from '@app/utils';
import {
  LoginHeader,
  Button,
  Link,
  Input,
  Password,
  Checkbox,
} from '@app/components';
import {
  Container,
  InnerContainer,
  Heading,
  Text,
  Seperator,
  RememberSection,
  Icon,
  ErrorMessage,
  SavingIndicator,
  IntroText,
  RowItem,
} from './sign-in-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '@app/providers';
import { getDefaultUrl } from '@app/routes/routes-helper';

export default function SignIn() {
  const { navigate } = useLocation();
  const { userInfo } = React.useContext(AuthContext);
  const [defaultUrl, setDefaultUrl] = React.useState(getDefaultUrl(userInfo));
  const { register, handleSubmit, errors } = useForm();
  const [createUserDB] = useCreateUserMutation();
  const [userError, setUserError] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');
  const [saving, updateSaving] = React.useState(false);
  const [addToken] = useAddOrRemoveToken();
  React.useEffect(() => {
    if (userInfo) {
      setTimeout(() => {
        setDefaultUrl(getDefaultUrl(userInfo));
        navigate(getDefaultUrl(userInfo));
      }, 50);
    }
    // eslint-disable-next-line
  }, [userInfo, navigate]);

  function handleChange(data: any) {
    if (userError && data.target.name === 'email') {
      setUserError('');
    }
    if (passwordError && data.target.name === 'password') {
      setPasswordError('');
    }
  }

  const setToken = async (currentUser: any, isGoogleSignIn?: boolean) => {
    const token: any = await getToken(currentUser);
    const params: any = {
      firebaseId: currentUser.uid,
      token: token.token,
      expiryDateTime: token.expiryDateTime.toString(),
      action: 'ADD',
    };
    await addToken({
      variables: params,
    })
      .then((result: any) => {
        if (
          result &&
          currentUser.uid &&
          result.data &&
          result.data.addOrRemoveFireBaseToken &&
          result.data.addOrRemoveFireBaseToken.success
        ) {
          updateSaving(false);
          setTimeout(() => {
            if (!isGoogleSignIn) {
              navigate(defaultUrl);
            }
          }, 500);
        } else {
          setUserError('Something went wrong please try again...');
        }
      })
      .catch((error: any) => {
        setUserError('Something went wrong please try again...');
      });
  };

  async function googleSignIn() {
    const currentUser = await signInWithGoogle();
    if (currentUser) {
      await setToken(currentUser, true).then(() => {
        const {
          uid,
          displayName,
          email,
          phoneNumber: phone,
          photoURL: profilePicUrl,
        } = currentUser;
        const [firstName, lastName] = displayName
          ? displayName.split(' ')
          : ['', ''];

        createUserDB({
          variables: {
            action: 'add',
            source: 'GOOGLE',
            firebaseId: uid,
            firstName,
            lastName,
            email,
            phone: phone || '',
            profilePicUrl,
          },
        })
          .then(async (res: any) => {})
          .catch((err: any) => {
            console.log(err);
          });
      });
    }
  }

  function onSubmit(data: any) {
    updateSaving(true);
    signIn(data.email, data.password)
      .then(async (res: any) => {
        await setToken(res);
      })
      .catch((err: any) => {
        updateSaving(false);
        if (err.code === 'auth/wrong-password') {
          setPasswordError('Invalid Password');
        }
        if (err.code === 'auth/user-not-found') {
          setUserError('User does not exist');
        }
        if (err.code === 'auth/invalid-email') {
          setUserError('Invalid email format');
        }
        if (err.code === 'auth/user-disabled') {
          setUserError(
            'Your account has been deactivated. Please contact Transfer Stamp support team',
          );
        }
      });
  }

  return (
    <>
      <LoginHeader location="sign-in" />
      <Container>
        <InnerContainer>
          <Heading>Sign In</Heading>
          <IntroText>Please sign in now to view transactions</IntroText>
          <Button
            variant="google"
            width="100%"
            size="large"
            onClick={() => googleSignIn()}>
            <Icon>Sign In with Google</Icon>
          </Button>
          <Seperator>
            <span> (OR) </span>
          </Seperator>
          <form onSubmit={handleSubmit(onSubmit)} onChange={handleChange}>
            <RowItem>
              <Input
                placeholder="Enter Email"
                register={register({ required: true })}
                name="email"
                type="text"
                label="Email"
              />

              {errors.email && <ErrorMessage>Please enter Email</ErrorMessage>}
              {!errors.email && userError && (
                <ErrorMessage>{userError}</ErrorMessage>
              )}
            </RowItem>
            <RowItem>
              <Password
                placeholder="Enter Password"
                register={register({ required: true })}
                name="password"
                label="Password"
              />
              {errors.password && (
                <ErrorMessage>Please enter Password</ErrorMessage>
              )}
              {!errors.password && passwordError && (
                <ErrorMessage>{passwordError}</ErrorMessage>
              )}
            </RowItem>
            <RememberSection>
              <Checkbox
                label="Remember Me"
                size="default"
                onChange={handleChange}
              />
              <Link href="/forgot-password" variant="primary">
                Forgot Password?
              </Link>
            </RememberSection>
            <Button variant="primary" size="large" width="100%" type="submit">
              Sign In{' '}
              {saving ? (
                <SavingIndicator>
                  <FontAwesomeIcon
                    color="#fff"
                    icon={faCircleNotch}
                    size="lg"
                    spin
                  />
                </SavingIndicator>
              ) : null}
            </Button>
          </form>
          <Text>
            Don't have an account ?{' '}
            <Link href="/sign-up" variant="secondary">
              Sign Up
            </Link>
          </Text>
        </InnerContainer>
      </Container>
    </>
  );
}
